import {
  WebGLRenderer,
  SRGBColorSpace,
  PCFShadowMap,
  PCFSoftShadowMap,
  VSMShadowMap,
  BasicShadowMap,
  LinearSRGBColorSpace,
  type ShadowMapType
} from "three";
import { BloomEffect } from "./effects/BloomEffect";
import { defaultRendererConfig } from "../../config/default.config";
import type {
  RendererConfig,
  RendererBaseConfig,
  ShadowType,
  OutputColorSpace,
  RenderMode,
  BloomParams,
  BlendParams
} from "./type";

/**
 * maxPixelRatio 설정에 따라 실제 pixelRatio 값을 계산하는 함수
 */
export function calculatePixelRatio(maxPixelRatio: number): number {
  return Math.min(window.devicePixelRatio, maxPixelRatio);
}

/**
 * 렌더러 크기를 설정하는 함수
 * @param updateStyle - CSS 스타일 업데이트 여부 (기본값: false, CSS 레이아웃 존중)
 */
export function setRendererSize(
  renderer: WebGLRenderer,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  pixelRatio: number,
  updateStyle: boolean = false
): void {
  renderer.setSize(width, height, false);
  renderer.setPixelRatio(pixelRatio);

  // 캔버스 버퍼 크기 설정 (렌더링 해상도)
  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;
  
  // CSS 스타일은 기본적으로 업데이트하지 않음 (CSS 레이아웃 존중)
  // updateStyle이 true인 경우에만 inline style 설정
  if (updateStyle) {
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }
}

/**
 * 캔버스 크기를 가져오는 함수
 */
export function getCanvasSize(
  canvas: HTMLCanvasElement,
  fallbackWidth: number,
  fallbackHeight: number
): { width: number; height: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    width: rect.width || fallbackWidth,
    height: rect.height || fallbackHeight
  };
}

/**
 * 렌더러 재생성을 위한 정리 함수
 */
export function disposeRenderer(renderer: WebGLRenderer): void {
  renderer.dispose();
}

/**
 * 블룸 효과 정리 함수
 */
export function disposeBloomEffect(bloomEffect: BloomEffect): void {
  bloomEffect.dispose();
}

/**
 * 섀도우 타입 문자열을 Three.js 상수로 변환하는 함수
 */
export function getShadowMapType(shadowType: ShadowType): ShadowMapType {
  switch (shadowType) {
    case "BasicShadowMap":
      return BasicShadowMap;
    case "PCFShadowMap":
      return PCFShadowMap;
    case "PCFSoftShadowMap":
      return PCFSoftShadowMap;
    case "VSMShadowMap":
      return VSMShadowMap;
    default:
      return PCFShadowMap;
  }
}

/**
 * 출력 색상 공간 문자열을 Three.js 상수로 변환하는 함수
 */
export function getOutputColorSpace(outputColorSpace: OutputColorSpace): string {
  switch (outputColorSpace) {
    case "srgb":
      return SRGBColorSpace;
    case "linear":
      return LinearSRGBColorSpace;
    case "p3":
      return SRGBColorSpace; // P3 대신 sRGB 사용
    case "rec2020":
      return SRGBColorSpace; // Rec2020 대신 sRGB 사용
    default:
      return SRGBColorSpace;
  }
}

/**
 * 렌더러 설정에서 블룸 효과 활성화 여부를 확인하는 함수
 */
export function isBloomEnabled(config: RendererConfig): boolean {
  return config.bloom?.enabled ?? true; // 기본값 true
}

/**
 * 블룸 효과를 렌더링하는 함수
 */
export function renderBloom(bloomEffect: BloomEffect): void {
  bloomEffect.render();
}

/**
 * 블룸 파라미터를 업데이트하는 함수
 */
export function updateBloomParams(
  bloomEffect: BloomEffect,
  params: Partial<BloomParams>
): void {
  bloomEffect.updateBloomParams(params);
}

/**
 * 블렌드 파라미터를 업데이트하는 함수
 */
export function updateBlendParams(
  bloomEffect: BloomEffect,
  params: Partial<BlendParams>
): void {
  bloomEffect.updateBlendParams(params);
}

/**
 * 블룸 효과 렌더 모드를 설정하는 함수
 */
export function setBloomRenderMode(
  bloomEffect: BloomEffect,
  mode: RenderMode
): void {
  bloomEffect.setRenderMode(mode);
}

/**
 * 블룸 객체를 하이라이트하는 함수
 */
export function highlightBloomedObjects(bloomEffect: BloomEffect): void {
  bloomEffect.highlightBloomedObjects();
}

/**
 * 블룸 파라미터를 가져오는 함수
 */
export function getBloomParams(bloomEffect: BloomEffect): BloomParams {
  return bloomEffect.getBloomParams();
}

/**
 * 블렌드 파라미터를 가져오는 함수
 */
export function getBlendParams(bloomEffect: BloomEffect): BlendParams {
  return bloomEffect.getBlendParams();
}

/**
 * 현재 렌더 모드를 가져오는 함수
 */
export function getCurrentRenderMode(bloomEffect: BloomEffect): RenderMode {
  return bloomEffect.getCurrentRenderMode();
}

/**
 * 초기 렌더러 크기를 설정하는 함수
 */
export function setInitialRendererSize(
  renderer: WebGLRenderer,
  canvas: HTMLCanvasElement
): void {
  const canvasRect = canvas.getBoundingClientRect();
  if (canvasRect.width > 0 && canvasRect.height > 0) {
    renderer.setSize(canvasRect.width, canvasRect.height, false);
  }
}

/**
 * 블룸 효과 크기를 조정하는 함수
 */
export function resizeBloomEffect(
  bloomEffect: BloomEffect,
  width: number,
  height: number
): void {
  bloomEffect.resize(width, height);
}

/**
 * 기본 렌더러 설정을 가져오는 함수
 */
export function getDefaultRendererConfig(): Required<RendererBaseConfig> {
  return { ...defaultRendererConfig.renderer } as Required<RendererBaseConfig>;
}

/**
 * 기본 블룸 파라미터를 가져오는 함수
 */
export function getDefaultBloomParams(): Required<BloomParams> {
  return { ...defaultRendererConfig.bloom?.config?.bloomParams } as Required<BloomParams>;
}

/**
 * 기본 블렌드 파라미터를 가져오는 함수
 */
export function getDefaultBlendParams(): Required<BlendParams> {
  return { ...defaultRendererConfig.bloom?.config?.blendParams } as Required<BlendParams>;
}
